import { useState, useEffect } from 'react';
import { X, Sparkles, AlertCircle, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import FilterBuilder, { createEmptyFilterConfig, hasConditions } from './FilterBuilder';
import { FilterConfig } from '../../types/filter';
import { mailApi } from '../../api/client';

export interface SmartColumnData {
  id?: string;
  displayName: string;
  sourceType: 'smart';
  sourceId: string;
  mailAccountId: string;
  filterConfig: FilterConfig;
  icon?: string;
}

interface SmartColumnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: SmartColumnData) => void;
  onDelete?: (columnId: string) => void;
  accounts: Array<{ id: string; providerEmail: string; provider: string }>;
  folders: Array<{ id: string; name: string; displayName: string; type: string }>;
  initialAccountId?: string;
  // Edit mode - pre-populate with existing column data
  editColumn?: {
    id: string;
    displayName: string;
    sourceId: string;
    mailAccountId: string;
    filterConfig?: FilterConfig;
    icon?: string;
  };
}

export default function SmartColumnModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  accounts,
  folders,
  initialAccountId,
  editColumn,
}: SmartColumnModalProps) {
  const isEditMode = !!editColumn;
  
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('🔍');
  const [accountId, setAccountId] = useState(initialAccountId || accounts[0]?.id || '');
  const [baseSource, setBaseSource] = useState<'all' | string>('all');
  const [filterConfig, setFilterConfig] = useState<FilterConfig>(createEmptyFilterConfig());
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize with edit data when modal opens
  useEffect(() => {
    if (isOpen && editColumn) {
      setName(editColumn.displayName || '');
      setIcon(editColumn.icon || '🔍');
      setAccountId(editColumn.mailAccountId || accounts[0]?.id || '');
      setBaseSource(editColumn.sourceId || 'all');
      setFilterConfig(editColumn.filterConfig || createEmptyFilterConfig());
      setError('');
      setShowDeleteConfirm(false);
    } else if (isOpen && !editColumn) {
      // Reset for new column
      setName('');
      setIcon('🔍');
      setAccountId(initialAccountId || accounts[0]?.id || '');
      setBaseSource('all');
      setFilterConfig(createEmptyFilterConfig());
      setError('');
      setShowDeleteConfirm(false);
    }
  }, [isOpen, editColumn, initialAccountId, accounts]);

  // Get categories for the selected account
  const { data: foldersData } = useQuery({
    queryKey: ['folders', accountId],
    queryFn: () => mailApi.getFolders(accountId),
    enabled: !!accountId,
  });

  const categories = (foldersData?.folders || [])
    .filter((f: any) => f.type === 'category')
    .map((f: any) => f.displayName || f.name);

  const availableFolders = folders.filter((f: any) => 
    f.type === 'folder' && !['deleteditems', 'drafts', 'sentitems', 'junkemail'].includes(f.name.toLowerCase())
  );

  const handleSave = () => {
    if (!name.trim()) {
      setError('Bitte gib einen Namen ein');
      return;
    }
    if (!hasConditions(filterConfig)) {
      setError('Bitte füge mindestens eine Bedingung hinzu');
      return;
    }

    onSave({
      id: editColumn?.id,
      displayName: name.trim(),
      sourceType: 'smart',
      sourceId: baseSource,
      mailAccountId: accountId,
      filterConfig,
      icon,
    });
  };

  const handleDelete = () => {
    if (editColumn?.id && onDelete) {
      onDelete(editColumn.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
          style={{ 
            backgroundColor: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border-default)',
          }}
        >
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {isEditMode ? 'Smart-Spalte bearbeiten' : 'Smart-Spalte erstellen'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-[auto_1fr] gap-4">
            {/* Icon Picker */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Icon
              </label>
              <div className="relative">
                <select
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  className="w-16 h-10 text-center text-xl rounded-lg border appearance-none cursor-pointer"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderColor: 'var(--border-default)',
                  }}
                >
                  {['🔍', '⭐', '📌', '🔥', '💡', '📊', '🎯', '💼', '📧', '🏷️', '🏠', '👥', '💬', '📝'].map((e) => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z.B. Wichtige von Kunden"
                className="w-full px-3 py-2 rounded-lg border"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          </div>

          {/* Account & Base Source */}
          <div className="grid grid-cols-2 gap-4">
            {/* Account */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Konto
              </label>
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--text-primary)',
                }}
              >
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.providerEmail}
                  </option>
                ))}
              </select>
            </div>

            {/* Base Source */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Basis-Ordner
              </label>
              <select
                value={baseSource}
                onChange={(e) => setBaseSource(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="all">Alle Mails</option>
                {availableFolders.map((folder: any) => (
                  <option key={folder.id} value={folder.id}>
                    📁 {folder.displayName || folder.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Filter Builder */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
              Filter-Regeln
            </label>
            <p className="text-xs mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Kombiniere Bedingungen mit UND/ODER. Nutze den "NICHT"-Button um Bedingungen zu negieren (z.B. "enthält NICHT").
            </p>
            <FilterBuilder
              value={filterConfig}
              onChange={setFilterConfig}
              categories={categories}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 text-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Delete Section (Edit Mode Only) */}
          {isEditMode && onDelete && (
            <div className="pt-4" style={{ borderTop: '1px solid var(--border-default)' }}>
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors"
                  style={{ color: 'var(--color-error)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Trash2 className="w-4 h-4" />
                  Spalte löschen
                </button>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                  <span className="text-sm" style={{ color: 'var(--color-error)' }}>
                    Wirklich löschen?
                  </span>
                  <button
                    onClick={handleDelete}
                    className="px-3 py-1 rounded text-sm font-medium bg-error text-white"
                  >
                    Ja, löschen
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1 rounded text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Abbrechen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 sticky bottom-0"
          style={{ 
            backgroundColor: 'var(--bg-primary)',
            borderTop: '1px solid var(--border-default)',
          }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-secondary)',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: 'var(--brand-primary)',
              color: 'white',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            {isEditMode ? 'Speichern' : 'Spalte erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}
